<template>
  <div class="card">
    <div class="card-header">
      <h4 class="title">Edit Profile</h4>
    </div>
    <div class="card-body">
      <div v-if="loading" class="spinner-container">
        <double-bounce size="60px"></double-bounce>
      </div>
      <div v-if="!loading && loadingError" class="alert alert-warning">
        <span>{{ loadingError }}</span>
      </div>
      <form v-if="!loading && user.id">
        <div class="row">
          <div class="col-md-6">
            <fg-input type="email" label="Email address" placeholder="Email" :disabled="true"
              v-model="user.emailAddress"></fg-input>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="control-label">&nbsp;</label>
              <br />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <fg-input type="password" label="Password" placeholder="****************" value="****************"
              :disabled="true"></fg-input>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label class="control-label">&nbsp;</label>
              <br />
              <p-button class="btn-default mr-1 mt-1" @click="showUpdatePasswordModal()">Update</p-button>
            </div>
          </div>
        </div>

        <!-- Modal for displaying withheld disbursements -->
        <div v-if="showPasswordUpdateModal" class="modal-wrapper">
          <!-- Modal backdrop when modal is open -->
          <div class="modal-backdrop fade show" @click="showPasswordUpdateModal = false"></div>

          <!-- Modal dialog -->
          <div class="modal fade show d-block" tabindex="-1" role="dialog">
            <div class="modal-dialog modal-dialog-centered modal-sm" role="document">
              <div class="modal-content">
                <div class="modal-header border-bottom">
                  <h5 class="modal-title">
                    <i class="fa fa-exclamation-triangle text-warning mr-2"></i>
                    Update Password
                    <button type="button" class="close" @click="showPasswordUpdateModal = false" aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                  </h5>

                </div>
                <form id="password-update" class="mx-auto mt-2 w-75">
                  <div class="form-group">
                    <label for="currentPassword">Current password</label>
                    <div class="input-group w-100">
                      <input class="form-control rounded-0" v-model="currentPassword" placeholder="Password"
                        :class="{ 'border-secondary': currentPasswordisFocused }"
                        :type="showCurrentPassword ? 'text' : 'password'" @focus="currentPasswordisFocused = true"
                        @blur="currentPasswordisFocused = false">
                      <div class="input-group-append">
                        <span class="input-group-text rounded-0 border-left-0"
                          :class="{ 'border-secondary': currentPasswordisFocused }"
                          @click="showCurrentPassword = !showCurrentPassword">
                          <i :class="showCurrentPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                        </span>
                      </div>
                    </div>
                    <small class="text-muted">The password you currently use to log in</small>
                  </div>

                  <div class="spacer"></div>

                  <div class="form-group">
                    <label for="password-container">New password</label>
                    <password-input @password-changed="handlePasswordValueChange"
                      @password-score-changed="handlePasswordScoreChange" />
                  </div>

                  <div class="spacer"></div>

                  <div class="form-group">
                    <label for="newPasswordConfirm">Confirm new password</label>
                    <div class="input-group w-100">
                      <input class="form-control rounded-0" v-model="newPasswordConfirm" placeholder="Password"
                        :class="{ 'border-secondary': passwordConfirmIsFocused }"
                        :type="showConfirmPassword ? 'text' : 'password'" @focus="passwordConfirmIsFocused = true"
                        @blur="passwordConfirmIsFocused = false">
                      <div class="input-group-append">
                        <span class="input-group-text rounded-0 border-left-0"
                          :class="{ 'border-secondary': passwordConfirmIsFocused }"
                          @click="showConfirmPassword = !showConfirmPassword">
                          <i :class="showConfirmPassword ? 'fa fa-eye' : 'fa fa-eye-slash'"></i>
                        </span>
                      </div>
                    </div>
                    <small class="text-muted">Please re-type your new password.</small>
                  </div>

                  <div class="spacer"></div>

                  <div v-if="user.mfaIsEnabled" class="form-group">
                    <label for="mfaToken">MFA Token</label>
                    <div class="d-flex w-100">
                      <input v-model="mfaToken" type="text" class="form-control rounded-0" placeholder="123456">
                    </div>
                  </div>

                  <p-button v-if="!working && (!this.passwordScore || this.passwordScore < 3)" round
                    class="mb-3 w-auto mx-auto d-block" disabled="true">
                    Unable to Reset &nbsp;
                    <i class="fa fa-times ml-2"></i>
                  </p-button>
                  <p-button v-else-if="!working && this.passwordScore > 2" type="submit" round block
                    class="mb-3 w-auto mx-auto d-block" @click.prevent="updateUserPassword">
                    Reset &nbsp;
                    <i class="fa fa-arrow-right ml-2"></i>
                  </p-button>
                  <p-button v-else-if="working && this.passwordScore > 2" type="info" round
                    class="btn-info mb-3 w-auto mx-auto d-block" disabled>
                    <i class="fa fa-spin fa-circle-o-notch"></i>&nbsp; Please wait&hellip;
                  </p-button>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <fg-input type="text" name="firstName" data-vv-as="first name" data-vv-delay="1000" label="First Name"
              placeholder="First Name" v-model="user.firstName" v-validate="formValidators.firstName" disabled="true"
              :error="fieldError('firstName')"></fg-input>
          </div>
          <div class="col-md-6">
            <fg-input type="text" name="lastName" data-vv-as="last name" data-vv-delay="1000" label="Last Name"
              placeholder="Last Name" v-model="user.lastName" v-validate="formValidators.lastName" disabled="true"
              :error="fieldError('lastName')"></fg-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <hr />
            <h6>
              <i class="fa fa-camera"></i>&nbsp;
              Profile picture
            </h6>
            <p>
              Profile pictures are managed by
              <a target="_blank" href="https://en.gravatar.com/support/what-is-gravatar/">Gravatar</a>. You can
              <a target="_blank" href="https://wordpress.com/me">update your profile</a> or set up your Gravatar account
              <a href="https://en.gravatar.com/support/how-to-sign-up/" target="_blank">using these instructions.</a>
            </p>
          </div>
        </div>

        <modal :show.sync="mfaModalOpen" class="modal-primary">
          <div slot="header">
            <span class="h5">Enroll for Multi-Factor Authentication</span>
          </div>

          <div v-if="mfaEnrollmentError" class="alert alert-warning" role="alert">
            Error enrolling for MFA: {{ mfaEnrollmentError }}
          </div>

          <div class="text-center">
            <p><strong>Step 1:</strong> scan the QR code below with your authenticator app such as
              <a :href="authyUrl" target="_blank">
                Authy
              </a> or
              <a :href="googleAuthUrl" target="_blank">
                Google Authenticator
              </a>.
            </p>

            <div class="border border-muted">
              <img class="mx-auto d-block" :src="mfaEnrollmentQRDataUrl" />
              <p class="text-muted">
                Can't see the QR code? Enter:<br>
                <code><mark>{{ mfaEnrollmentSecret }}</mark></code>
              </p>
            </div>

            <p class="mt-5">
              <strong>Step 2:</strong> enter the 6 digit code from your authenticator app and
              press confirm before it expires.
            </p>

            <input type="text" class="form-control" placeholder="XXXXXX" v-model="mfaEnrollmentToken"
              inputmode="numeric" pattern="[0-9]*" />
            <button class="btn btn-success"
              :disabled="mfaEnrollmentWorking || !mfaEnrollmentToken || mfaEnrollmentToken.length < 6"
              @click.prevent="confirmMfaEnrollment">
              <span v-if="mfaEnrollmentWorking"><i class="fa fa-spinner fa-spin"></i>&nbsp; Please wait&hellip;</span>
              <span v-else><i class="fa fa-check"></i>&nbsp; Confirm</span>
            </button>
            <p class="text-muted mt-3">
              Need help? <router-link to="/support">Contact support</router-link>.
            </p>
          </div>
        </modal>

        <div class="row">
          <div class="col-md-12">
            <hr />
            <h6>
              <i class="fa fa-lock"></i>&nbsp;
              Multi Factor Authentication
            </h6>
            <p class="mb-0">Secure your account by requiring a time-based one-time password from your device when you
              log
              in.</p>

            <div>
              <a v-if="!user.mfaIsEnabled" class="btn btn-sm btn-primary text-light" @click="enrollMfa">
                <i class="fa fa-lock mr-3"></i>Enrol
              </a>
              <a v-else class="btn btn-sm btn-danger text-light" @click="disableMfa">
                <i class="fa fa-unlock mr-2"></i>Disable
              </a>
            </div>
          </div>
        </div>

      </form>
    </div>
  </div>
</template>
<script>
import _ from "lodash";
import { DoubleBounce } from "vue-loading-spinner";
import { Modal } from "src/components/UIComponents";
import getDeviceId from "../../../../../device";
import PasswordInput from "../../../../UIComponents/Inputs/Password.vue";

export default {
  components: {
    DoubleBounce,
    Modal,
    PasswordInput
  },
  props: {
    user: Object
  },
  data() {
    return {
      formValidators: {
        firstName: {
          required: false,
          min: 2
        },
        lastName: {
          required: false,
          min: 1
        }
      },
      currentPassword: '',
      password: '',
      newPasswordConfirm: '',
      mfaToken: null,
      passwordScore: null,
      validationErrors: {},
      saving: false,
      loading: false,
      showPasswordUpdateModal: false,
      showCurrentPassword: false,
      showConfirmPassword: false,
      currentPasswordisFocused: false,
      passwordConfirmIsFocused: false,
      working: false,
      loadingError: null,
      mfaEnrollmentWorking: false,
      mfaEnrollmentQRDataUrl: null,
      mfaEnrollmentSecret: null,
      mfaEnrollmentRecoveryCode: null,
      mfaEnrollmentToken: null,
      mfaEnrollmentError: null,
      mfaModalOpen: false,
      authyUrl: 'https://authy.com/download/',
      googleAuthUrl: 'https://support.google.com/accounts/answer/1066447',
      refreshKey: 0
    };
  },
  methods: {
    enrollMfa() {
      this.refreshKey++;
      this.mfaEnrollmentWorking = true;
      const requestBody = { type: 'totp' }
      this.axios.put(`/v1/users/${this.user.id}/mfa-enrollment`, requestBody)
        .then((response) => {
          this.mfaEnrollmentQRDataUrl = response.data.qrDataUrl;
          this.mfaEnrollmentSecret = response.data.secret;
          this.mfaEnrollmentRecoveryCode = response.data.recoveryCode;
        })
        .catch((error) => {
          this.mfaEnrollmentError = error.response.data.message;
        })

      this.mfaEnrollmentWorking = false;
      this.mfaModalOpen = true;
    },
    confirmMfaEnrollment() {
      const tokenIsNumeric = /^\d+$/.test(this.mfaEnrollmentToken);
      if (!this.mfaEnrollmentToken
        || !tokenIsNumeric) {
        this.mfaEnrollmentError = "Invalid code provided. Please double check your authenticator app.";
      } else {
        this.mfaEnrollmentWorking = true;
        const requestBody = { type: 'totp', token: this.mfaEnrollmentToken }

        this.axios.put(`v1/users/${this.user.id}/mfa-enrollment/confirmation`, requestBody)
          .then((response) => {
            this.mfaEnrollmentWorking = false;

            if (response.status !== 200) {
              this.mfaEnrollmentError = response.statusText;
              this.mfaEnrollmentWorking = false;
              return
            }

            this.mfaModalOpen = false;

            const user = this.user;
            user.mfaIsEnabled = true;
            this.$store.commit("SET_USER", user);

            this.$swal({
              title: 'Single Use Recovery Code',
              icon: 'info',
              input: 'text',
              backdrop: 'rgba(0,0,0,0.4)',
              html: `
                <span style="font-size: 18px;" class="badge rounded-pill bg-secondary text-light mb-2">${this.mfaEnrollmentRecoveryCode}</span><br> 
                <p>MFA has been successfully enabled. Note the above recovery code somewhere and keep it safe. You can use it to sign in if you lose access to your device.</p>
              `,
              allowOutsideClick: false,
              // allowEscapeKey: false,
              inputLabel: 'Type "Confirm" below to dismiss.',
              inputPlaceholder: 'Confirm',
              inputValidator: (value) => {
                return new Promise((resolve) => {
                  if (value.toLowerCase().trim() === 'confirm') {
                    resolve()
                  } else {
                    resolve('You need to type "Confirm" in the text box to acknowledge you have copied your recovery code before proceeding.')
                  }
                })
              }
            });
          })
          .catch((error) => {
            this.mfaEnrollmentError = error.response.data.message;
            this.mfaEnrollmentWorking = false;
          });
      }
    },
    disableMfa() {
      this.$swal({
        title: `Disable MFA for ${this.user.displayName}?`,
        input: 'text',
        icon: 'question',
        backdrop: 'rgba(0,0,0,0.4)',
        confirmButtonText: 'Disable',
        confirmButtonColor: '#dc3545',
        inputLabel: 'Enter your authenticator code (or recovery code) to confirm.',
        inputPlaceholder: 'XXXXXX',
        inputValidator: (value) => {
          return new Promise((resolve) => {
            if (value.length === 6 || value.length === 24) {
              this.axios.delete(`v1/users/${this.user.id}/mfa-enrollment`, { data: { mfaToken: value } })
                .then((response) => {
                  this.$swal({
                    icon: 'success',
                    title: 'MFA Successfully disabled',
                    toast: true,
                    timeout: 3000
                  })
                  const user = this.user;
                  user.mfaIsEnabled = false;
                  this.$store.commit("SET_USER", user);
                  this.refreshKey++;
                })
                .catch((error) => {
                  this.$swal({
                    icon: 'error',
                    title: 'Something went wrong. Please try again.',
                    toast: true,
                    timeout: 3000
                  })
                })
              resolve()
            } else {
              resolve('Invalid code provided. Please double check your authenticator app.')
            }
          })
        }
      })
    },
    handlePasswordValueChange(value) {
      this.password = value;
    },
    handlePasswordScoreChange(value) {
      this.passwordScore = value;
    },
    showUpdatePasswordModal: function () {
      // Show modal
      this.showPasswordUpdateModal = true;
    },
    async updateUserPassword() {

      if (this.password == this.user.emailAddress) {
        return this.$swal(
          'Cannot use your email address as a password.'
        );
      }

      if (this.password != this.newPasswordConfirm) {
        return this.$swal(
          `Oops, your passwords don't match.`
        );
      }

      this.working = true

      function delay(t) {
        return new Promise(function (resolve) {
          setTimeout(resolve, t);
        });
      }
      const passwordUpdatePayload = {
        currentPassword: this.currentPassword,
        newPassword: this.password,
      }

      if (this.mfaToken) {
        passwordUpdatePayload.mfaToken = this.mfaToken
      }
      await axios
        .put(`/v1/users/${this.user.id}/password`, passwordUpdatePayload)
        .then(() => {
          // wait a short while to avoid race conditions
          return delay(2000);
        })
        .then(() => {
          const accessTokenRefreshPayload = {
            type: "refresh",
            grantType: "password",
            emailAddress: this.user.emailAddress,
            password: this.password,
            deviceIdentifier: getDeviceId(),
          }
          if (this.mfaToken) {
            passwordUpdatePayload.mfaToken = this.mfaToken
          }
          // now need to get a new refresh token, since the old one will be considered invalid
          return this.axios.post("/v1/tokens", accessTokenRefreshPayload);
        })
        .then((response) => {
          // set the refresh token expiry
          const expSeconds =
            moment(response.data.expiresAt).unix() - moment().unix();
          this.$ls.set(
            "refresh_token_expires_at",
            response.data.expiresAt,
            expSeconds * 1000
          );
          this.showPasswordUpdateModal = false;
          this.working = false;
          this.currentPassword = '',
            this.password = '',
            this.newPasswordConfirm = '',
            this.mfaToken = '',
            this.$store.dispatch("userUpdated");
          return this.$swal('Password has been succesfully updated')
        })
        .catch((error) => {
          this.working = false;

          if (error.response.data.message && error.response.data.message == 'Password appears in list of compromised passwords') {
            return this.$swal(error.response.data.message);
          }

          if (error.response.data.message && error.response.data.message == 'Invalid format: Password contains words from prohibited list') {
            return this.$swal(error.response.data.message);
          }

          let errMsg = "Error updating password";

          if (error.response && error.response.data) {
            if (error.response.data.message) {
              errMsg += `: ${error.response.data.message}.`;
            }

            // try to do field-level error handling
            if (
              error.response.data.errors &&
              error.response.data.errors.length > 0
            ) {
              _.each(error.response.data.errors, (err) => {
                if (err.messages && err.messages.length > 0) {
                  errMsg += `<br>${err.messages[0]}`;
                }
              });
            }
          }
          return this.$swal(errMsg)
        });
    },
    updateUser() {
      this.saving = true;
      const body = {
        firstName: this.user.firstName,
        lastName: this.user.lastName
      };
      this.axios
        .patch(`/v1/users/${this.user.id}`, body)
        .then(response => {
          this.saving = false;
          // this.user = response.data;
          this.$store.commit("SET_USER", response.data)

          this.$notify({
            message: "Profile updated",
            timeout: 2000,
            icon: "fa fa-check",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "success"
          });
        })
        .catch(error => {
          this.saving = false;
          let errorMessage = "Error updating profile";
          this.$notify({
            message: errorMessage,
            timeout: 2000,
            icon: "fa fa-exclamation-triangle",
            horizontalAlign: "right",
            verticalAlign: "top",
            type: "error"
          });
        });
    },
    validate() {
      this.$validator.validateAll().then(isValid => {
        this.$emit("on-submit", this.registerForm, isValid);
        if (isValid) {
          this.updateUser();
        }
      });
    },
    fieldError(fieldName) {
      // clientside
      if (this.errors.first(fieldName)) {
        return this.errors.first(fieldName);
      }

      // serverside
      const fieldError = _.find(this.validationErrors, item => {
        return _.includes(item.field, fieldName);
      });

      if (!fieldError) {
        return false;
      } else if (fieldError.messages) {
        return fieldError.messages;
      } else {
        return true;
      }
    }
  },
  beforeMount() {
    if (!this.user) {
      this.loading = true;
      this.axios
        .get("/v1/users/current")
        .then(response => {
          this.loading = false;
          this.user = response.data;
        })
        .catch(error => {
          this.loading = false;
          this.loadingError = "Failed to load user";
        });
    }
  }
};
</script>
<style></style>
